import { apiClient, handleResponse, handleError } from '../helpers';
import { api } from '../constants';
import { disconnectAndReset } from './socket';
import { shutdownLogRocketSession } from '../observability';
import {
    createRequestCacheKey,
    fetchWithCache,
    invalidateRequestCache,
} from '../helpers/requestCache';
import {
    clearMediaPreloadCache,
    collectMediaUrls,
    extractLandingMediaUrls,
    preloadMediaUrls,
} from '../helpers/mediaPreloader';

const LANDING_MEDIA_TTL_MS = 30 * 60 * 1000;
const PROFILE_AVATARS_TTL_MS = 10 * 60 * 1000;
const FAV_ACTIVITIES_TTL_MS = 30 * 60 * 1000;
const TERMS_TTL_MS = 30 * 60 * 1000;

const landingMediaCacheKey = () => createRequestCacheKey('user', 'landing-media');
const profileAvatarsCacheKey = (studentId) => createRequestCacheKey('user', 'profile-avatars', studentId);
const favoriteActivitiesCacheKey = () => createRequestCacheKey('user', 'favorite-activities');
const termsCacheKey = (popupType) => createRequestCacheKey('user', 'terms-and-conditions', popupType);

export const userService = {
    login,
    logout,
    register,
    fetchLandingMedia,
    fetchprofileavatars,
    fetchFavActivities,
    updatefavactivity,
    fetchTermsandConditions,
    updateTermsandConditions,
    collectCoin
};

function login(AccessCode) {
    const requestOptions = { AccessCode };
    // Use explicit method: 'POST' so the login endpoint is never called as GET (e.g. after a redirect).
    return apiClient.request({
        method: 'POST',
        url: api.user.login,
        data: requestOptions,
    })
        .then(handleResponse)
        .then(user => {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            // localStorage.setItem('user', JSON.stringify(user));
            return user;
        })
        .catch(error => handleError(error.response));
}

function logout() {
    // end LogRocket session so the logged-in session is closed and not mixed with post-logout activity
    shutdownLogRocketSession();
    // disconnect socket so no background polling continues after logout
    disconnectAndReset();
    invalidateRequestCache();
    clearMediaPreloadCache();
    // remove user from local storage to log user out
    localStorage.removeItem('user');
    localStorage.removeItem('adMessageDisplayed');
}

function register(user) {
    return apiClient.post(api.user.signup, user)
        .then(handleResponse)
        .then(data => {
            invalidateRequestCache(createRequestCacheKey('profile', 'myprofile'));
            return data;
        })
        .catch(error => handleError(error.response));
}

function fetchLandingMedia() {
    return fetchWithCache({
        key: landingMediaCacheKey(),
        ttlMs: LANDING_MEDIA_TTL_MS,
        loader: () => apiClient.get(api.user.landingMedia)
            .then(handleResponse)
            .then(response => {
                const landingMedia = response?.data ?? response ?? [];
                preloadMediaUrls(extractLandingMediaUrls(landingMedia));
                return landingMedia;
            }),
    })
        .catch(error => handleError(error.response));
}

function fetchprofileavatars(studentid) {
    return fetchWithCache({
        key: profileAvatarsCacheKey(studentid),
        ttlMs: PROFILE_AVATARS_TTL_MS,
        loader: () => apiClient.get(`${api.user.profileAvatar}/${studentid}`)
            .then(handleResponse)
            .then(response => {
                const avatars = response?.data ?? response ?? [];
                preloadMediaUrls(collectMediaUrls(avatars, ['Media_URL']));
                return avatars;
            }),
    })
        .catch(error => handleError(error.response));
}

function fetchFavActivities() {
    return fetchWithCache({
        key: favoriteActivitiesCacheKey(),
        ttlMs: FAV_ACTIVITIES_TTL_MS,
        loader: () => apiClient.get(api.user.fetchFavactivities)
            .then(handleResponse)
            .then(response => response?.data ?? response ?? []),
    })
        .catch(error => handleError(error.response));
}

function updatefavactivity(activityDetails) {
    return apiClient.put(api.user.updateFavactivities, activityDetails)
        .then(handleResponse)
        .then(data => {
            invalidateRequestCache([
                favoriteActivitiesCacheKey(),
                createRequestCacheKey('profile', 'myprofile'),
            ]);
            return data;
        })
        .catch(error => handleError(error.response));
}

function fetchTermsandConditions(popupType) {
    return fetchWithCache({
        key: termsCacheKey(popupType),
        ttlMs: TERMS_TTL_MS,
        loader: () => apiClient.get(`${api.user.fetchTermsandConditions}/${popupType}`)
            .then(handleResponse)
            .then(response => response?.data ?? response ?? []),
    })
        .catch(error => handleError(error.response));
}

function updateTermsandConditions(submissionData) {
    return apiClient.post(api.user.updateTermsandConditions, submissionData)
        .then(handleResponse)
        .then(data => {
            return data;
        })
        .catch(error => handleError(error.response));
}

function collectCoin(coinData) {
    return apiClient.post(api.user.collectCoin, coinData)
        .then(handleResponse)
        .then(data => {
            invalidateRequestCache(createRequestCacheKey('profile', 'mypoints'));
            return data;
        })
        .catch(error => handleError(error.response));
}
